import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { hostApi } from "@/lib/hostApi";
import { buildLiveWsUrl } from "@/lib/liveApi";
import { useHostAuth } from "@/contexts/HostAuthContext";
import { toast } from "sonner";
import {
  Play, Pause, Stop, Users, Copy, Monitor, ArrowLeft, Lightning, Warning,
  Eye, SkipForward, Plus, Minus, Stairs, HandWaving, Sparkle, Coin,
} from "@phosphor-icons/react";

/**
 * /live/:code/host — Host Event Control.
 * Phase 2: lobby management (open/close lobby, view players, launch, end).
 * Phase 3 will add question-runner controls (next / reveal / adjust score / etc.).
 */
export default function HostEventControl() {
  const { code } = useParams();
  const { host, loading } = useHostAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentQ, setCurrentQ] = useState(null);
  const [answerCount, setAnswerCount] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!host) return;
    let closed = false;
    let retryTimer = null;
    let retryDelay = 1000;

    const hydrate = () => hostApi.get(`/live/events/${code}`).then((r) => {
      setEvent(r.data.event);
      setPlayers(r.data.players || []);
      setCurrentQ(r.data.current_question || null);
    }).catch((e) => setError(e.response?.data?.detail || "Event not found"));

    const connect = () => {
      const ws = new WebSocket(buildLiveWsUrl({ code, role: "host" }));
      wsRef.current = ws;
      ws.onopen = () => { retryDelay = 1000; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "snapshot") {
            setEvent(msg.data.event);
            setPlayers(msg.data.players || []);
            setCurrentQ(msg.data.current_question || null);
          } else if (msg.type === "lobby_updated") {
            setPlayers(msg.data.players || []);
          } else if (msg.type === "state_changed" || msg.type === "event_config_updated") {
            setEvent(msg.data);
          } else if (msg.type === "question_started") {
            setCurrentQ(msg.data);
            setAnswerCount(0);
          } else if (msg.type === "answers_progress") {
            setAnswerCount(msg.data.count);
          } else if (msg.type === "question_ended") {
            setCurrentQ((prev) => prev ? {
              ...prev, revealed: true,
              answer_index: msg.data.correct_index,
              accepted_answer: msg.data.correct_answer,
            } : prev);
            setPlayers(msg.data.scoreboard || []);
          } else if (msg.type === "scoreboard_updated") {
            setPlayers(msg.data.scoreboard || []);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 10000);
          hydrate(); connect();
        }, retryDelay);
      };
    };
    hydrate(); connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [code, host]);

  if (loading) return <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center"><p className="text-sm text-nk-mute">Loading…</p></div>;
  if (!host) return <Navigate to="/host/login" replace />;
  if (error) return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex flex-col items-center justify-center gap-3 px-6">
      <Warning size={26} className="text-nk-bad" />
      <p className="text-sm text-nk-bad" data-testid="host-event-error">{error}</p>
      <Link to="/host/console" className="text-xs text-nk-gold hover:underline">← Back to Host Console</Link>
    </div>
  );
  if (!event) return <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center"><p className="text-sm text-nk-mute">Loading event…</p></div>;

  const transition = async (nextState) => {
    setBusy(true);
    try {
      await hostApi.post(`/live/events/${event.code}/state`, { state: nextState });
      // WS will push the new state; toast for confirmation
      const label = nextState === "lobby" ? "Lobby opened"
                  : nextState === "running" ? "Event launched"
                  : nextState === "paused" ? "Event paused"
                  : nextState === "ended" ? "Event ended"
                  : "Updated";
      toast.success(label);
      if (nextState === "ended") setTimeout(() => navigate("/host/console"), 800);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const joinUrl = `${window.location.origin}/live/${event.code}/play`;
  const screenUrl = `${window.location.origin}/live/${event.code}/screen`;
  const copy = (v, label) => navigator.clipboard.writeText(v).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Copy failed — long-press to copy manually"),
  );

  const nextQuestion = async () => {
    setBusy(true);
    try {
      await hostApi.post(`/live/events/${event.code}/questions/next`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const revealAnswer = async () => {
    setBusy(true);
    try {
      await hostApi.post(`/live/events/${event.code}/questions/reveal`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const adjustScore = async (tempid, delta) => {
    try {
      await hostApi.post(`/live/events/${event.code}/scores/adjust`, { player_tempid: tempid, delta });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveLadderConfig = async (ladderPatch) => {
    setBusy(true);
    try {
      await hostApi.patch(`/live/events/${event.code}`, { config: { ladder: ladderPatch } });
      toast.success("Ladder saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setLadderContestant = async (tempid) => {
    try {
      await hostApi.post(`/live/events/${event.code}/ladder/contestant`, { player_tempid: tempid });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ladderWalkAway = async (tempid) => {
    if (!window.confirm("Lock in this contestant's current score and take them off the ladder?")) return;
    try {
      await hostApi.post(`/live/events/${event.code}/ladder/walk-away`, { player_tempid: tempid });
      toast.success("Contestant walked away with current score");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const isLadder = event.mode === "ladder";
  const ladderCfg = event.config?.ladder;
  const activeContestant = event.ladder_active_contestant;
  const isSolo = ladderCfg?.gameplay_flow === "solo";

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text" data-testid="host-event-control">
      <header className="border-b border-nk-line/60 bg-nk-bg/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/host/console" data-testid="host-back-to-console" className="inline-flex items-center gap-1 text-xs text-nk-mute hover:text-nk-text">
            <ArrowLeft size={14} /> Console
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{event.title}</p>
            <p className="text-[10px] text-nk-mute font-mono">
              {event.mode.toUpperCase()} · {event.template} · <b className="text-nk-gold">{event.code}</b>
              <span className="mx-2">·</span>
              <span className="text-nk-gold uppercase">{event.state}</span>
            </p>
          </div>
          <a
            href={screenUrl} target="_blank" rel="noopener noreferrer"
            data-testid="host-open-canvas"
            className="inline-flex items-center gap-1.5 text-xs text-nk-gold hover:opacity-80 border border-nk-gold/40 rounded-sm px-3 py-1.5"
          >
            <Monitor size={12} /> Open Public Canvas
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Share links */}
        <section className="nk-card p-5 space-y-3" data-testid="host-share-links">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">Share</p>
          <ShareRow label="Player join link" value={joinUrl} onCopy={() => copy(joinUrl, "Join link")} testid="share-join" />
          <ShareRow label="Public Canvas (TV / OBS)" value={screenUrl} onCopy={() => copy(screenUrl, "Screen URL")} testid="share-screen" />
        </section>

        {/* Ladder configuration (only for ladder events in draft/lobby) */}
        {isLadder && (event.state === "draft" || event.state === "lobby") && (
          <LadderConfigPanel
            ladder={ladderCfg}
            onSave={saveLadderConfig}
            busy={busy}
          />
        )}

        {/* Controls */}
        <section className="nk-card p-5 space-y-3" data-testid="host-controls">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">Controls</p>
          <div className="flex items-center gap-3 flex-wrap">
            {event.state === "draft" && (
              <ControlBtn onClick={() => transition("lobby")} disabled={busy} Icon={Users} label="Open lobby" data-testid="ctl-open-lobby" primary />
            )}
            {event.state === "lobby" && (
              <>
                <ControlBtn onClick={() => transition("running")} disabled={busy || players.length < 1} Icon={Play} label="Launch event" data-testid="ctl-launch" primary />
                <ControlBtn onClick={() => transition("draft")} disabled={busy} Icon={ArrowLeft} label="Close lobby" data-testid="ctl-close-lobby" />
              </>
            )}
            {event.state === "running" && (
              <>
                <ControlBtn onClick={() => transition("paused")} disabled={busy} Icon={Pause} label="Pause" data-testid="ctl-pause" />
                <ControlBtn onClick={() => transition("ended")} disabled={busy} Icon={Stop} label="End event" data-testid="ctl-end" danger />
              </>
            )}
            {event.state === "paused" && (
              <>
                <ControlBtn onClick={() => transition("running")} disabled={busy} Icon={Play} label="Resume" data-testid="ctl-resume" primary />
                <ControlBtn onClick={() => transition("ended")} disabled={busy} Icon={Stop} label="End event" data-testid="ctl-end" danger />
              </>
            )}
            {event.state === "ended" && (
              <p className="text-xs text-nk-mute">Event has ended. Return to console to review or delete.</p>
            )}
          </div>
          {event.state === "lobby" && players.length === 0 && (
            <p className="text-[11px] text-nk-mute inline-flex items-center gap-1">
              <Lightning size={11} weight="fill" className="text-nk-gold" />
              Waiting for at least one player before you can launch.
            </p>
          )}
        </section>

        {/* Players in room (with scores + host adjust buttons) */}
        <section className="nk-card p-5" data-testid="host-players-panel">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 inline-flex items-center gap-2">
              <Users size={12} weight="fill" /> Scoreboard · {players.length} players
            </p>
          </div>
          {players.length === 0 ? (
            <p className="text-sm text-nk-mute text-center py-6">No one has joined yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="host-scoreboard">
              {players.map((p, i) => {
                const isActive = isLadder && isSolo && activeContestant === p.player_tempid;
                const isElim = isLadder && p.ladder_eliminated;
                const isWalked = isLadder && p.ladder_walked_away;
                return (
                <li
                  key={p.player_tempid}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm border transition-colors ${
                    isActive ? "border-nk-gold bg-nk-gold/10" :
                    isElim ? "border-nk-line/40 opacity-50" :
                    isWalked ? "border-nk-line/50 opacity-70" :
                    "border-nk-line/50 hover:border-nk-gold/40"
                  }`}
                  data-testid={`host-player-row-${p.player_tempid}`}
                >
                  <span className="font-mono text-[10px] text-nk-gold w-6">#{i + 1}</span>
                  <span className="text-2xl">{p.avatar || "🎮"}</span>
                  <span className="flex-1 text-sm truncate">
                    {p.display_name}
                    {isActive && <span className="ml-2 text-[9px] font-mono uppercase text-nk-gold">on the hook</span>}
                    {isElim && <span className="ml-2 text-[9px] font-mono uppercase text-nk-bad">out</span>}
                    {isWalked && <span className="ml-2 text-[9px] font-mono uppercase text-nk-mute">walked</span>}
                  </span>
                  <span className="font-mono text-sm text-nk-gold" data-testid={`host-player-score-${p.player_tempid}`}>
                    {isLadder && ladderCfg?.reward_type === "cash"
                      ? `${ladderCfg?.currency || "USD"} ${p.score || 0}`
                      : (p.score || 0)}
                  </span>
                  {(event.state === "running" || event.state === "paused") && (
                    <span className="flex items-center gap-1 ml-2">
                      {isLadder && isSolo && !isElim && !isWalked && !isActive && (
                        <button
                          onClick={() => setLadderContestant(p.player_tempid)}
                          data-testid={`ladder-set-active-${p.player_tempid}`}
                          className="px-2 h-6 text-[10px] uppercase font-mono rounded-sm border border-nk-gold/50 text-nk-gold hover:bg-nk-gold/10"
                          title="Send to hot seat"
                        >Seat</button>
                      )}
                      {isLadder && !isElim && !isWalked && (p.ladder_current_rung ?? -1) >= 0 && (
                        <button
                          onClick={() => ladderWalkAway(p.player_tempid)}
                          data-testid={`ladder-walk-away-${p.player_tempid}`}
                          className="px-2 h-6 text-[10px] uppercase font-mono rounded-sm border border-nk-line text-nk-mute hover:text-nk-gold hover:border-nk-gold/60"
                          title="Lock in current score"
                        >Walk</button>
                      )}
                      {!isLadder && (
                        <>
                          <button
                            onClick={() => adjustScore(p.player_tempid, -5)}
                            data-testid={`adjust-minus-${p.player_tempid}`}
                            className="w-6 h-6 rounded-sm border border-nk-line text-nk-mute hover:text-nk-bad hover:border-nk-bad/60 flex items-center justify-center"
                            title="-5"
                          ><Minus size={11} /></button>
                          <button
                            onClick={() => adjustScore(p.player_tempid, 5)}
                            data-testid={`adjust-plus-${p.player_tempid}`}
                            className="w-6 h-6 rounded-sm border border-nk-line text-nk-mute hover:text-nk-gold hover:border-nk-gold/60 flex items-center justify-center"
                            title="+5"
                          ><Plus size={11} /></button>
                        </>
                      )}
                    </span>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Question runner (only during running) */}
        {event.state === "running" && (
          <section className="nk-card p-5" data-testid="host-runner">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">
                Question runner
                {currentQ && (
                  <span className="ml-2 text-nk-text">
                    · Q{currentQ.index} of {currentQ.total}
                  </span>
                )}
              </p>
              <p className="text-[11px] text-nk-mute font-mono">
                {answerCount > 0 && `${answerCount}/${players.length} answered`}
              </p>
            </div>

            {!currentQ ? (
              <div className="text-center py-6">
                <p className="text-sm text-nk-mute mb-3">
                  {isLadder
                    ? isSolo && !activeContestant
                      ? "Seat a contestant first (tap Seat next to a player)."
                      : "Ready to serve Rung 1 whenever you are."
                    : "Ready to start Q1 whenever you are."}
                </p>
                <ControlBtn
                  onClick={nextQuestion}
                  disabled={busy || players.length === 0 || (isLadder && isSolo && !activeContestant)}
                  Icon={Play}
                  label={isLadder ? "Start Rung 1" : "Start first question"}
                  data-testid="ctl-next-q" primary
                />
              </div>
            ) : (
              <>
                {isLadder && currentQ.ladder?.rung && (
                  <div className="mb-3 flex items-center gap-3 px-3 py-2 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06]">
                    <Stairs size={16} weight="fill" className="text-nk-gold" />
                    <p className="text-sm">
                      <span className="text-nk-mute font-mono text-[10px] uppercase mr-2">Rung {currentQ.ladder.rung_index + 1}</span>
                      <span className="text-nk-gold font-mono">
                        {currentQ.ladder.reward_type === "cash"
                          ? `${currentQ.ladder.currency_symbol}${currentQ.ladder.rung.amount.toLocaleString()}`
                          : `${currentQ.ladder.rung.amount} pts`}
                      </span>
                      {currentQ.ladder.rung.safe && (
                        <span className="ml-2 text-[10px] font-mono uppercase text-nk-gold">safe · checkpoint</span>
                      )}
                    </p>
                  </div>
                )}
                <p className="text-[10px] uppercase text-nk-gold/70 font-mono mb-1">
                  {currentQ.category || "—"}
                  {currentQ.type && currentQ.type !== "multiple_choice" && (
                    <span className="ml-2 text-nk-gold">· {currentQ.type.replace(/_/g, " ")}</span>
                  )}
                </p>
                <p className="text-lg font-medium mb-3">{currentQ.body}</p>
                {currentQ.type === "unscramble" && currentQ.scrambled && (
                  <div className="mb-3 py-3 px-3 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] text-center">
                    <p className="font-mono text-[9px] uppercase text-nk-gold/70 mb-1">Scrambled</p>
                    <p className="font-display text-2xl tracking-[0.3em] text-nk-gold">{currentQ.scrambled}</p>
                  </div>
                )}
                {(currentQ.type === "multiple_choice" || !currentQ.type) ? (
                <ul className="space-y-2 mb-4" data-testid="host-question-options">
                  {(currentQ.options || []).map((o, i) => {
                    const isCorrect = currentQ.revealed && currentQ.answer_index === i;
                    return (
                      <li
                        key={i}
                        className={`px-3 py-2 rounded-sm border text-sm flex items-center gap-3 ${
                          isCorrect ? "border-nk-gold bg-nk-gold/10 text-nk-gold" : "border-nk-line"
                        }`}
                      >
                        <span className="font-mono text-xs text-nk-mute w-4">{String.fromCharCode(65 + i)}.</span>
                        <span className="flex-1">{o}</span>
                        {isCorrect && <span className="text-[10px] font-mono uppercase text-nk-gold">Correct</span>}
                      </li>
                    );
                  })}
                </ul>
                ) : (
                <div className="mb-4 px-3 py-3 rounded-sm border border-nk-line" data-testid="host-text-answer">
                  <p className="font-mono text-[10px] uppercase text-nk-mute mb-1">Accepted answer</p>
                  <p className="font-display text-xl text-nk-gold">{currentQ.accepted_answer || (currentQ.revealed ? "—" : "· Hidden until reveal ·")}</p>
                </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  {!currentQ.revealed && (
                    <ControlBtn
                      onClick={revealAnswer} disabled={busy}
                      Icon={Eye} label="Reveal answer" data-testid="ctl-reveal" primary
                    />
                  )}
                  {currentQ.revealed && currentQ.index < currentQ.total && (
                    <ControlBtn
                      onClick={nextQuestion} disabled={busy}
                      Icon={SkipForward}
                      label={isLadder ? `Next rung (${currentQ.index + 1}/${currentQ.total})` : "Next question"}
                      data-testid="ctl-next-q" primary
                    />
                  )}
                  {currentQ.revealed && currentQ.index >= currentQ.total && (
                    <p className="text-sm text-nk-gold" data-testid="ctl-all-done">
                      {isLadder
                        ? `All ${currentQ.total} rungs played. End the event to lock scores.`
                        : `All ${currentQ.total} questions played. End the event to declare a winner.`}
                    </p>
                  )}
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}


function ShareRow({ label, value, onCopy, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-nk-dim font-mono mb-1">{label}</p>
      <div className="flex items-center gap-2 bg-nk-bg/60 px-3 py-2 rounded-sm border border-nk-line">
        <code className="text-[11px] font-mono text-nk-text flex-1 truncate">{value}</code>
        <button
          onClick={onCopy}
          data-testid={testid}
          className="inline-flex items-center gap-1 text-xs text-nk-gold hover:opacity-80"
        >
          <Copy size={12} /> Copy
        </button>
      </div>
    </div>
  );
}


function ControlBtn({ onClick, disabled, Icon, label, primary, danger, ...rest }) {
  const cls = danger
    ? "border border-nk-bad text-nk-bad hover:bg-nk-bad/10"
    : primary
      ? "bg-nk-gold text-nk-bg hover:opacity-90"
      : "border border-nk-line text-nk-text hover:border-nk-gold/60";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 text-sm font-medium rounded-sm inline-flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
      {...rest}
    >
      <Icon size={14} weight="bold" />
      {label}
    </button>
  );
}


// -----------------------------------------------------------------------------
// Ladder Configuration Panel — visible for ladder events in draft/lobby
// -----------------------------------------------------------------------------
const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "NGN", symbol: "₦" },
  { code: "GHS", symbol: "₵" },
  { code: "KES", symbol: "KSh" },
  { code: "ZAR", symbol: "R" },
];

function LadderConfigPanel({ ladder, onSave, busy }) {
  const initial = ladder || {};
  const [rewardType, setRewardType] = useState(initial.reward_type || "points");
  const [currency, setCurrency] = useState(initial.currency || "USD");
  const [rungCount, setRungCount] = useState(initial.rung_count || 10);
  const [distributionMode, setDistributionMode] = useState(initial.distribution_mode || "auto");
  const [curve, setCurve] = useState(initial.auto_curve || (rewardType === "cash" ? "exponential" : "linear"));
  const [pool, setPool] = useState(initial.auto_total_pool || (rewardType === "cash" ? 10000 : 5500));
  const [gameplayFlow, setGameplayFlow] = useState(initial.gameplay_flow || "simultaneous");
  const [rungs, setRungs] = useState(initial.rungs || []);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    setRewardType(initial.reward_type || "points");
    setCurrency(initial.currency || "USD");
    setRungCount(initial.rung_count || 10);
    setDistributionMode(initial.distribution_mode || "auto");
    setCurve(initial.auto_curve || (initial.reward_type === "cash" ? "exponential" : "linear"));
    setPool(initial.auto_total_pool || (initial.reward_type === "cash" ? 10000 : 5500));
    setGameplayFlow(initial.gameplay_flow || "simultaneous");
    setRungs(initial.rungs || []);
  }, [ladder?.rung_count, ladder?.reward_type, ladder?.currency, ladder?.distribution_mode, ladder?.auto_curve, ladder?.auto_total_pool, ladder?.gameplay_flow]);

  const currencySymbol = useMemo(
    () => (CURRENCIES.find((c) => c.code === currency)?.symbol || "$"),
    [currency],
  );
  const totalPot = useMemo(() => rungs.reduce((s, r) => s + (r.amount || 0), 0), [rungs]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await hostApi.post("/live/events/ladder/preview", {
        pool: Number(pool), rung_count: Number(rungCount),
        curve, reward_type: rewardType,
      });
      setRungs(data.rungs);
      toast.success("Preview generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const editRungAmount = (idx, value) => {
    setRungs((prev) => prev.map((r, i) => i === idx ? { ...r, amount: Math.max(0, Number(value) || 0) } : r));
  };

  const handleSave = () => {
    onSave({
      reward_type: rewardType,
      currency,
      rung_count: Number(rungCount),
      distribution_mode: distributionMode,
      auto_curve: curve,
      auto_total_pool: Number(pool),
      gameplay_flow: gameplayFlow,
      rungs: distributionMode === "manual" ? rungs : undefined,
    });
  };

  return (
    <section className="nk-card p-5 space-y-4" data-testid="ladder-config-panel">
      <div className="flex items-center gap-2">
        <Stairs size={14} className="text-nk-gold" weight="fill" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">Ladder configuration</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldGroup label="Reward type">
          <Segmented
            value={rewardType} onChange={setRewardType} testid="ladder-reward-type"
            options={[
              { value: "points", label: "Points", Icon: Sparkle },
              { value: "cash", label: "Cash", Icon: Coin },
            ]}
          />
        </FieldGroup>
        {rewardType === "cash" && (
          <FieldGroup label="Currency">
            <select
              value={currency} onChange={(e) => setCurrency(e.target.value)}
              data-testid="ladder-currency"
              className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{`${c.symbol} · ${c.code}`}</option>
              ))}
            </select>
          </FieldGroup>
        )}
        <FieldGroup label="Rungs">
          <input
            type="number" min={10} max={12}
            value={rungCount} onChange={(e) => setRungCount(Math.max(10, Math.min(12, Number(e.target.value) || 10)))}
            data-testid="ladder-rung-count"
            className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
          />
          <p className="text-[10px] text-nk-mute mt-1">Every 3rd rung is a safe checkpoint.</p>
        </FieldGroup>
        <FieldGroup label="Gameplay flow">
          <Segmented
            value={gameplayFlow} onChange={setGameplayFlow} testid="ladder-gameplay-flow"
            options={[
              { value: "simultaneous", label: "All players", Icon: Users },
              { value: "solo", label: "Solo (hot seat)", Icon: HandWaving },
            ]}
          />
        </FieldGroup>
      </div>

      <div className="border-t border-nk-line/60 pt-4 space-y-3">
        <FieldGroup label="Amount distribution">
          <Segmented
            value={distributionMode} onChange={setDistributionMode} testid="ladder-distribution-mode"
            options={[
              { value: "auto", label: "Auto" },
              { value: "manual", label: "Manual" },
            ]}
          />
        </FieldGroup>
        {distributionMode === "auto" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <FieldGroup label="Curve">
              <Segmented
                value={curve} onChange={setCurve} testid="ladder-curve"
                options={[
                  { value: "linear", label: "Linear" },
                  { value: "exponential", label: "Exponential" },
                ]}
              />
            </FieldGroup>
            <FieldGroup label={`Total pool ${rewardType === "cash" ? `(${currencySymbol})` : "(pts)"}`}>
              <input
                type="number" min={0}
                value={pool} onChange={(e) => setPool(Number(e.target.value) || 0)}
                data-testid="ladder-pool"
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none font-mono"
              />
            </FieldGroup>
            <button
              onClick={runPreview} disabled={previewing}
              data-testid="ladder-preview-btn"
              className="px-3 py-2 text-sm rounded-sm bg-nk-gold/15 text-nk-gold border border-nk-gold/40 hover:bg-nk-gold/25 disabled:opacity-40"
            >
              {previewing ? "Previewing…" : "Auto-distribute"}
            </button>
          </div>
        )}

        {/* Rungs table */}
        {rungs.length > 0 && (
          <div className="space-y-1.5" data-testid="ladder-rungs-list">
            <div className="grid grid-cols-[3rem_1fr_5rem] gap-2 text-[10px] uppercase font-mono text-nk-mute px-2">
              <span>Rung</span><span>Amount</span><span className="text-right">Type</span>
            </div>
            {rungs.map((r, i) => (
              <div key={i} className={`grid grid-cols-[3rem_1fr_5rem] gap-2 items-center px-2 py-1.5 rounded-sm border ${
                r.safe ? "border-nk-gold/40 bg-nk-gold/5" : "border-nk-line/40"
              }`} data-testid={`ladder-rung-${i}`}>
                <span className="font-mono text-sm text-nk-gold">#{i + 1}</span>
                {distributionMode === "manual" ? (
                  <input
                    type="number" min={0} value={r.amount}
                    onChange={(e) => editRungAmount(i, e.target.value)}
                    data-testid={`ladder-rung-amount-${i}`}
                    className="px-2 py-1 rounded-sm border border-nk-line bg-nk-bg text-sm font-mono focus:border-nk-gold outline-none"
                  />
                ) : (
                  <span className="font-mono text-sm">
                    {rewardType === "cash" ? `${currencySymbol}${r.amount.toLocaleString()}` : `${r.amount} pts`}
                  </span>
                )}
                <span className="text-right text-[10px] font-mono uppercase text-nk-gold">
                  {r.safe ? "safe" : "—"}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 text-xs">
              <span className="text-nk-mute">Total pot</span>
              <span className="font-mono text-nk-gold" data-testid="ladder-total-pot">
                {rewardType === "cash" ? `${currencySymbol}${totalPot.toLocaleString()}` : `${totalPot} pts`}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end pt-2">
        <button
          onClick={handleSave} disabled={busy}
          data-testid="ladder-save-btn"
          className="px-4 py-2 text-sm rounded-sm bg-nk-gold text-nk-bg font-medium hover:opacity-90 disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save ladder"}
        </button>
      </div>
    </section>
  );
}


function FieldGroup({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-mono text-nk-mute mb-1">{label}</label>
      {children}
    </div>
  );
}


function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-sm border border-nk-line overflow-hidden" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`}
          className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-2 transition-colors ${
            value === o.value ? "bg-nk-gold text-nk-bg" : "bg-transparent text-nk-mute hover:text-nk-text"
          }`}
        >
          {o.Icon && <o.Icon size={12} weight="bold" />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
